import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ttApi, ttAdminApi } from "../../lib/ttApi";
import { Loader2, Plus, Trash2, KeyRound, Shield, ArrowLeft, Users, Copy } from "lucide-react";

// Admin-only screen. Super-admin creates / disables / regenerates test-tech
// credentials and provisions programs. The page itself calls the existing
// super-admin-authenticated CSuite JWT so no new auth path is needed.
export default function AdminTestTechs() {
  const [techs, setTechs] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [openProgram, setOpenProgram] = useState(false);
  const [credsToast, setCredsToast] = useState(null); // { username, password }

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const [t, p] = await Promise.all([
        ttAdminApi.listTechs(),
        // list programs — we reuse the public /test-tech/programs through a thin shim
        // that works because super-admins can create but there's no "list all" endpoint.
        // For MVP: parse program_ids out of techs and dedupe. Add a dedicated list later.
        Promise.resolve({ items: [] }),
      ]);
      setTechs(t.items || []);
      // Build the program list from the techs' program_ids (deduped). The admin
      // can create new ones; names come from the create response.
      const seen = new Set();
      const progs = [];
      (t.items || []).forEach(x => (x.program_ids || []).forEach(id => {
        if (!seen.has(id)) { seen.add(id); progs.push({ _id: id, name: id }); }
      }));
      setPrograms(progs);
    } catch (e) { setErr(e.message || "Load failed"); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const removeOne = async (id) => {
    if (!window.confirm("Permanently delete this test-tech account?")) return;
    try { await ttAdminApi.deleteTech(id); setTechs(techs.filter(t => t._id !== id)); }
    catch (e) { alert(e.message); }
  };

  const toggleActive = async (t) => {
    try {
      const upd = await ttAdminApi.updateTech(t._id, { active: !t.active });
      setTechs(techs.map(x => x._id === t._id ? upd : x));
    } catch (e) { alert(e.message); }
  };

  const regeneratePw = async (t) => {
    const npw = `TT-${Math.random().toString(36).slice(2, 8)}-${Math.floor(Math.random()*9000+1000)}`;
    try {
      await ttAdminApi.updateTech(t._id, { new_password: npw });
      setCredsToast({ username: t.username, password: npw });
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin/orgs" className="text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 text-xs" data-testid="admin-tt-back">
            <ArrowLeft size={14}/> Admin
          </Link>
          <span className="font-display text-lg font-extrabold tracking-tight">Test Tech Accounts</span>
          <span className="pill pill-neutral text-[9px]">SUPER ADMIN</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setOpenProgram(true)} className="btn btn-secondary btn-sm inline-flex items-center gap-1" data-testid="admin-tt-create-program">
            <Plus size={13}/> New Program
          </button>
          <button onClick={() => setOpenCreate(true)} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="admin-tt-create">
            <Plus size={13}/> New Test Tech
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8" data-testid="page-admin-test-techs">
        {err && <div className="p-3 bg-red-50 border-l-4 border-red-500 text-sm text-red-800 mb-4">{err}</div>}

        {busy ? (
          <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="animate-spin" size={14}/> Loading…</div>
        ) : (
          <div className="bg-white rounded-sm border border-zinc-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-tt-table">
              <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2">Username</th>
                  <th className="text-left px-3 py-2">Display name</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Programs</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {techs.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-10 text-center text-zinc-500">
                    No test-tech accounts yet. Click <b>New Test Tech</b> to create one.
                  </td></tr>
                )}
                {techs.map(t => (
                  <tr key={t._id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`admin-tt-row-${t._id}`}>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-900">{t.username}</td>
                    <td className="px-3 py-2">{t.display_name}</td>
                    <td className="px-3 py-2 text-xs text-zinc-500">{t.email || "—"}</td>
                    <td className="px-3 py-2 text-xs text-zinc-500 font-mono">{(t.program_ids || []).length || "none"}</td>
                    <td className="px-3 py-2">
                      <span className={`pill text-[9px] ${t.active ? "pill-green" : "pill-neutral"}`}>{t.active ? "ACTIVE" : "DISABLED"}</span>
                    </td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <button onClick={() => toggleActive(t)} className="text-xs text-zinc-600 hover:text-zinc-900 underline underline-offset-2" data-testid={`admin-tt-toggle-${t._id}`}>
                        {t.active ? "Disable" : "Enable"}
                      </button>
                      <button onClick={() => regeneratePw(t)} className="text-xs text-amber-700 hover:text-amber-900 underline underline-offset-2 inline-flex items-center gap-1" data-testid={`admin-tt-regen-${t._id}`}>
                        <KeyRound size={11}/> Regen pw
                      </button>
                      <button onClick={() => removeOne(t._id)} className="text-xs text-red-700 hover:text-red-900 underline underline-offset-2 inline-flex items-center gap-1" data-testid={`admin-tt-delete-${t._id}`}>
                        <Trash2 size={11}/> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 text-xs text-zinc-500">
          <Shield size={11} className="inline mr-1"/> Test-tech users sign in at <code className="bg-zinc-100 px-1 py-0.5 rounded-sm">/test-tech</code>. Share credentials securely — regenerate the password after onboarding.
        </div>
      </main>

      {openCreate && <CreateTechModal programs={programs} onClose={() => setOpenCreate(false)} onCreated={(created, pw) => {
        setTechs([created, ...techs]);
        setOpenCreate(false);
        setCredsToast({ username: created.username, password: pw });
      }}/>}
      {openProgram && <CreateProgramModal onClose={() => setOpenProgram(false)} onCreated={(p) => {
        setPrograms([...programs, p]);
        setOpenProgram(false);
      }}/>}
      {credsToast && <CredsToast creds={credsToast} onClose={() => setCredsToast(null)} />}
    </div>
  );
}


function CreateTechModal({ programs, onClose, onCreated }) {
  const [f, setF] = useState({ username: "", password: "", display_name: "", email: "", program_ids: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.username.length < 3 || f.password.length < 8 || !f.display_name) {
      setErr("Username 3+ chars, password 8+ chars, display name required."); return;
    }
    setBusy(true);
    try {
      const payload = { ...f };
      if (!payload.email) delete payload.email;
      const created = await ttAdminApi.createTech(payload);
      onCreated(created, f.password);
    } catch (e) { setErr(e.message || "Create failed"); }
    finally { setBusy(false); }
  };

  const togglePid = (id) =>
    setF({ ...f, program_ids: f.program_ids.includes(id) ? f.program_ids.filter(x => x !== id) : [...f.program_ids, id] });

  return (
    <Modal onClose={onClose} title="Create Test Tech" testid="admin-tt-create-modal">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Username" value={f.username} onChange={v => setF({ ...f, username: v })} testid="admin-tt-username"/>
        <Field label="Password (shown once, store securely)" value={f.password} onChange={v => setF({ ...f, password: v })} testid="admin-tt-password" type="text"/>
        <Field label="Display name" value={f.display_name} onChange={v => setF({ ...f, display_name: v })} testid="admin-tt-display"/>
        <Field label="Email (optional)" value={f.email} onChange={v => setF({ ...f, email: v })} testid="admin-tt-email" type="email"/>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Programs</div>
          {programs.length === 0 ? (
            <div className="text-xs text-zinc-500">No programs yet — create one first, then edit the user.</div>
          ) : (
            <div className="space-y-1">
              {programs.map(p => (
                <label key={p._id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={f.program_ids.includes(p._id)} onChange={() => togglePid(p._id)} data-testid={`admin-tt-prog-${p._id}`}/>
                  <span className="font-mono text-xs">{p.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        {err && <div className="p-2 bg-red-50 border-l-4 border-red-500 text-sm text-red-800">{err}</div>}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
          <button type="submit" disabled={busy} className="btn btn-primary btn-sm" data-testid="admin-tt-create-submit">
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
}


function CreateProgramModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", code: "", sponsor: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.name) { setErr("Program name required."); return; }
    setBusy(true);
    try {
      const p = await ttAdminApi.createProgram(f);
      onCreated(p);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="New Program" testid="admin-prog-modal">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name" value={f.name} onChange={v => setF({ ...f, name: v })} testid="admin-prog-name"/>
        <Field label="Code (short)" value={f.code} onChange={v => setF({ ...f, code: v })} testid="admin-prog-code"/>
        <Field label="Sponsor" value={f.sponsor} onChange={v => setF({ ...f, sponsor: v })} testid="admin-prog-sponsor"/>
        <Field label="Description" value={f.description} onChange={v => setF({ ...f, description: v })} testid="admin-prog-desc"/>
        {err && <div className="p-2 bg-red-50 border-l-4 border-red-500 text-sm text-red-800">{err}</div>}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
          <button type="submit" disabled={busy} className="btn btn-primary btn-sm" data-testid="admin-prog-submit">
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
}


function CredsToast({ creds, onClose }) {
  const copy = () => { try { navigator.clipboard.writeText(`${creds.username} / ${creds.password}`); } catch {} };
  return (
    <div className="fixed bottom-6 right-6 z-[90] max-w-sm bg-white border-l-4 border-emerald-500 shadow-2xl rounded-sm p-4" data-testid="admin-tt-creds-toast">
      <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-700 mb-1">Credentials — shown once</div>
      <div className="font-display text-sm font-bold">Share these with the tester securely</div>
      <div className="mt-2 p-2 bg-zinc-50 border border-zinc-200 rounded-sm font-mono text-xs">
        <div><b>User:</b> {creds.username}</div>
        <div><b>Pass:</b> {creds.password}</div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <button onClick={copy} className="text-xs text-emerald-700 hover:text-emerald-900 underline inline-flex items-center gap-1" data-testid="admin-tt-creds-copy">
          <Copy size={11}/> Copy
        </button>
        <button onClick={onClose} className="text-xs text-zinc-500 hover:text-zinc-800">Dismiss</button>
      </div>
    </div>
  );
}


function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center px-4" style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }} onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-md shadow-2xl max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
        <div className="font-display text-lg font-extrabold tracking-tight mb-3">{title}</div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="fld py-2 px-3 w-full" data-testid={testid}/>
    </label>
  );
}
